import { get, post, put, del, uploadFile } from './client.js';
import type {
  ProductDetail,
  ProductCategory,
  ProductImage,
  ProductDocument,
  ProductListResponse,
  CreateProductRequest,
  UpdateProductRequest,
  ProductTranslation,
} from '../types/index.js';

export const productApi = {
  listCategories: () => get<ProductCategory[]>('/products/categories'),
  createCategory: (name: string) => post<ProductCategory>('/products/categories', { name }),
  updateCategory: (id: number, data: { name?: string; sort_order?: number }) =>
    put<ProductCategory>(`/products/categories/${id}`, data),
  deleteCategory: (id: number) => del(`/products/categories/${id}`),

  list: (params?: { page?: number; per_page?: number; category_id?: number; keyword?: string; lang?: string }) => {
    const q = new URLSearchParams();
    if (params?.page) q.append('page', params.page.toString());
    if (params?.per_page) q.append('per_page', params.per_page.toString());
    if (params?.category_id !== undefined) q.append('category_id', params.category_id.toString());
    if (params?.keyword) q.append('keyword', params.keyword);
    if (params?.lang) q.append('lang', params.lang);
    const qs = q.toString();
    return get<ProductListResponse>(`/products${qs ? '?' + qs : ''}`);
  },

  get: (id: number, lang?: string) => get<ProductDetail>(`/products/${id}${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`),
  getDetail: (id: number, lang?: string) => get<ProductDetail>(`/products/${id}${lang ? `?lang=${encodeURIComponent(lang)}` : ''}`),
  listTranslations: (id: number) => get<ProductTranslation[]>(`/products/${id}/translations`),
  replaceTranslations: (id: number, translations: ProductTranslation[]) =>
    put<ProductTranslation[]>(`/products/${id}/translations`, { translations }),
  deleteTranslation: (id: number, lang: string) => del(`/products/${id}/translations/${encodeURIComponent(lang)}`),

  create: (data: CreateProductRequest) =>
    post<{ id: number; name: string; category_id: number }>('/products', data),

  update: (id: number, data: UpdateProductRequest) => put<ProductDetail>(`/products/${id}`, data),

  delete: (id: number) => del(`/products/${id}`),

  getEmailCard: (id: number) => get<{ html: string }>(`/products/${id}/email-card`),

  deleteImage: (productId: number, imageId: number) => del(`/products/${productId}/images/${imageId}`),

  deleteDoc: (productId: number, docId: number) => del(`/products/${productId}/documents/${docId}`),

  reorderImages: (productId: number, imageIds: number[]) =>
    put(`/products/${productId}/images/sort`, { image_ids: imageIds }),
};

export function uploadProductImage(
  productId: number,
  file: File,
  isPrimary: boolean,
  onProgress?: (percent: number) => void,
): Promise<{ data: ProductImage }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('is_primary', isPrimary ? '1' : '0');
  return uploadFile(`/products/${productId}/images`, fd, onProgress);
}

export function uploadProductDocument(
  productId: number,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ data: ProductDocument }> {
  const fd = new FormData();
  fd.append('file', file);
  return uploadFile(`/products/${productId}/documents`, fd, onProgress);
}
